// @givmo/fundraisers-module — requests to join a campaign, the organizers' side (FCM-WEB33; the mockups
// Jonathan ruled as D-FCM-35, 2026-10-17 10:41 PDT; backend FCM-BE47). Built once for both consoles: the
// class enters only as the server's data.
//
// Where each sits (decision 1): people asking to start a page under the campaign on the Campaign tab,
// right under the Joining card that sets who may ask; other organizations' campaigns asking to become
// its layers on the Layers tab, above the tree. Each tab's label carries its waiting count. Approving a
// person is one act (their page is made then, by the join); approving a campaign confirms first, since it
// changes the structure and every total above. A decline is final for that person or campaign, so it
// confirms too (decision 5). Answers are the moderation rank's (OWNER); anyone who reads the campaign
// reads the requests, which carry the name and team a person typed and never more (decision 4).
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  isGivmoError,
  sdk,
  type P2pJoinRequestOut,
  type P2pLayerRequestOut,
} from '@givmo/api-client';
import {
  Badge,
  Button,
  Card,
  CardBody,
  DataTable,
  EmptyState,
  Icon,
  cn,
  testIdProp,
  toast,
  type DataTableColumn,
} from '@givmo/design-system';
import { CircleAlert, CircleCheck, ExternalLink, Inbox, Info } from 'lucide-react';
import { ConfirmDialog } from './confirm-dialog';
import { campaignOptions } from './campaign-queries';
import { REQUESTS_COPY as R } from './join-requests-copy';
import {
  campaignRequestsOptions,
  invalidateRequestReads,
  peopleRequestsOptions,
} from './join-requests-queries';
import { LAYERS_COPY as C } from './layers-copy';
import { invalidateLayerReads } from './layers-queries';
import { PHONE_HIT, StateNotice } from './lifecycle-ui';
import { displayP2pDate } from './p2p-decode';
import { recipientIdsOf, useRecipientNames, type RecipientIds } from './recipient-names';
import { useLivePage } from './use-live-page';

/** A refused answer: the server's own words, else the module's. */
function answerRefusal(error: unknown): string {
  return isGivmoError(error) && error.message ? error.message : R.answerFailed;
}

/** A read the backend does not serve (dark, or older): its absent route, never an error to show. */
function notServed(error: unknown): boolean {
  return isGivmoError(error) && error.status === 404;
}

/**
 * The waiting count on a tab's label, in the module's count pill (the "Started by supporters" tab's).
 * Nothing at zero.
 */
export function TabCount({ count, testId }: { count: number | null | undefined; testId: string }) {
  if (count == null || count <= 0) return null;
  return (
    <span
      className="inline-flex h-20 min-w-20 items-center justify-center rounded-full bg-neutral-100 px-6 text-s12 font-w600 leading-none tabular-nums text-text-secondary"
      aria-label={R.waitingCount(count)}
      {...testIdProp(testId)}
    >
      {count}
    </span>
  );
}

/** "Approved Oct 5, 2026", or "Cancelled Jun 30, 2026, when it ended": a decided request's line. */
function decidedLine(
  request: { status: string; answered_at?: string | null; expires_at: string },
  why: string | null,
): string {
  const label = R.status[request.status] ?? request.status;
  const at = request.status === 'expired' ? request.expires_at : (request.answered_at ?? null);
  const line = at ? R.decidedOn(label, displayP2pDate(at)) : label;
  return why ? `${line}, ${why}` : line;
}

/** The team a person chose, or the new team they named (public once approved). */
function teamOf(request: P2pJoinRequestOut): string {
  return request.new_team_name ? R.newTeam(request.new_team_name) : (request.team_name ?? R.noTeam);
}

function hasPicks(list: RecipientIds | null | undefined): boolean {
  return recipientIdsOf(list).length > 0;
}

/** What a person's page would raise for, where its campaign's pages pick, by name. */
function PicksNames({ list }: { list: RecipientIds | null | undefined }) {
  const names = useRecipientNames(list);
  return <>{names.items.map((item) => item.name).join(', ')}</>;
}

/** The failed read's one state, with a way to read again. */
function RequestsUnavailable({ testId, onRetry }: { testId: string; onRetry: () => void }) {
  return (
    <EmptyState
      testId={testId}
      title={R.loadFailed}
      action={
        <Button appearance="saas" tone="secondary" testId={`${testId}-retry`} onClick={onRetry}>
          Try again
        </Button>
      }
    />
  );
}

// ── People's requests (the Campaign tab) ──────────────────────────────────────────────────────────

export function PeopleRequests({
  campaignId,
  campaignName,
  canAnswer,
  ended,
}: {
  campaignId: string;
  campaignName: string;
  /** The moderation rank on this campaign: Approve and Decline. */
  canAnswer: boolean;
  /** The campaign has ended: its Joining card says the waiting requests were cancelled. */
  ended: boolean;
}) {
  const client = useQueryClient();
  const live = useLivePage();
  const read = useQuery({ ...peopleRequestsOptions(campaignId), retry: false });
  const [declining, setDeclining] = useState<P2pJoinRequestOut | null>(null);
  const reread = () =>
    Promise.all([
      invalidateRequestReads(client),
      client.invalidateQueries({ queryKey: campaignOptions(campaignId).queryKey }),
    ]);
  const answer = useMutation({
    mutationFn: async ({
      request,
      act,
    }: {
      request: P2pJoinRequestOut;
      act: 'approve' | 'decline';
    }) => {
      const path = { fundraiser_id: campaignId, request_id: request.id };
      if (act === 'approve')
        await sdk.campaignsApproveCampaignJoinRequest({ path, throwOnError: true });
      else await sdk.campaignsDeclineCampaignJoinRequest({ path, throwOnError: true });
    },
    onSuccess: async (_data, { act }) => {
      await reread();
      if (live()) {
        setDeclining(null);
        toast.success(act === 'approve' ? R.approvedToast : R.declinedToast);
      }
    },
    // A refused answer leaves the request as the server holds it: its words, and the list re-reads.
    onError: (error) => {
      if (!live()) return;
      setDeclining(null);
      toast.error(answerRefusal(error));
      void reread();
    },
  });

  if (read.isPending || (read.isError && notServed(read.error))) return null;
  const rows = read.data ?? [];
  // A Team column only where a request names a team, and Raises for only where pages pick (02).
  const teams = rows.some((request) => request.team_name != null || request.new_team_name != null);
  const picks = rows.some((request) => hasPicks(request.picks));
  const busyOn = (request: P2pJoinRequestOut, act: 'approve' | 'decline') =>
    answer.isPending && answer.variables?.request.id === request.id && answer.variables.act === act;
  const why = (request: P2pJoinRequestOut) =>
    // A person's request is cancelled only by its campaign's end; an ended campaign says so once.
    request.status === 'cancelled' && !ended ? R.whenThisEnded : null;
  const acts = (request: P2pJoinRequestOut, phone = false) =>
    request.status === 'pending' && canAnswer ? (
      <div className={cn('flex items-center gap-8', phone ? 'flex-wrap' : 'justify-end')}>
        <Button
          appearance="saas"
          tone="secondary"
          size="sm"
          className={PHONE_HIT}
          disabled={answer.isPending}
          onClick={() => setDeclining(request)}
          testId={`requests-person-decline-${request.id}`}
        >
          {R.decline}
        </Button>
        <Button
          appearance="saas"
          size="sm"
          className={PHONE_HIT}
          loading={busyOn(request, 'approve')}
          disabled={answer.isPending}
          onClick={() => answer.mutate({ request, act: 'approve' })}
          testId={`requests-person-approve-${request.id}`}
        >
          {R.approve}
        </Button>
      </div>
    ) : request.status !== 'pending' ? (
      <div className={cn('flex', !phone && 'justify-end')}>
        <Badge tone="neutral">{R.status[request.status] ?? request.status}</Badge>
      </div>
    ) : null;
  const columns: DataTableColumn<P2pJoinRequestOut>[] = [
    {
      id: 'supporter',
      header: R.colSupporter,
      width: picks ? '22%' : '28%',
      cell: (request) => <span className="font-w600 text-text-strong">{request.display_name}</span>,
    },
    ...(teams
      ? [
          {
            id: 'team',
            header: R.colTeam,
            cell: (request: P2pJoinRequestOut) => (
              <span className="text-text-secondary">{teamOf(request)}</span>
            ),
          },
        ]
      : []),
    ...(picks
      ? [
          {
            id: 'picks',
            header: R.colRaisesFor,
            cell: (request: P2pJoinRequestOut) => (
              <span className="text-text-secondary">
                <PicksNames list={request.picks} />
              </span>
            ),
          },
        ]
      : []),
    {
      id: 'asked',
      header: R.colAsked,
      width: '232px',
      cell: (request) => (
        <div className="flex flex-col gap-2 tabular-nums">
          <span className="text-text-secondary">{displayP2pDate(request.created_at)}</span>
          <span className="text-s12 text-text-secondary">
            {request.status === 'pending'
              ? R.expires(displayP2pDate(request.expires_at))
              : decidedLine(request, why(request))}
          </span>
        </div>
      ),
    },
    { id: 'acts', header: '', align: 'right', width: '196px', cell: (request) => acts(request) },
  ];
  const empty = (
    <EmptyState
      testId="requests-people-empty"
      icon={<Icon icon={Inbox} size={20} aria-hidden />}
      title={R.emptyTitle}
      description={R.emptyPeople}
    />
  );
  return (
    <section className="flex flex-col gap-12" data-testid="requests-people">
      <div className="min-w-0">
        <h2 className="text-s16 font-w600 text-text-strong">{R.requestsTitle}</h2>
        <p className="mt-2 text-s13 leading-h15 text-text-secondary">{R.peopleSubtitle}</p>
      </div>
      {read.isError ? (
        <Card flush>
          <CardBody>
            <RequestsUnavailable
              testId="requests-people-error"
              onRetry={() => void read.refetch()}
            />
          </CardBody>
        </Card>
      ) : (
        <>
          <div className="hidden lg:block">
            <DataTable
              testId="requests-people-table"
              columns={columns}
              rows={rows}
              rowKey={(request) => request.id}
              rowTestId={(request) => `requests-person-row-${request.id}`}
              emptyState={empty}
            />
          </div>
          <div className="flex flex-col gap-12 lg:hidden" data-testid="requests-people-cards">
            {rows.map((request) => (
              <Card key={request.id} flush testId={`requests-person-card-${request.id}`}>
                <CardBody className="flex flex-col gap-8 p-16">
                  <div className="flex items-start justify-between gap-12">
                    <h3 className="text-s14 font-w600 text-text-strong">{request.display_name}</h3>
                    {request.status !== 'pending' && (
                      <Badge tone="neutral" className="shrink-0">
                        {R.status[request.status] ?? request.status}
                      </Badge>
                    )}
                  </div>
                  {(teams || hasPicks(request.picks)) && (
                    <p className="text-s12 leading-h15 text-text-secondary">
                      {teams ? teamOf(request) : null}
                      {teams && hasPicks(request.picks) ? ' · ' : null}
                      {hasPicks(request.picks) ? (
                        <>
                          {R.colRaisesFor} <PicksNames list={request.picks} />
                        </>
                      ) : null}
                    </p>
                  )}
                  <p className="text-s12 leading-h15 text-text-secondary">
                    {R.asked(displayP2pDate(request.created_at))} ·{' '}
                    {request.status === 'pending'
                      ? R.expires(displayP2pDate(request.expires_at))
                      : decidedLine(request, why(request))}
                  </p>
                  {request.status === 'pending' && canAnswer && (
                    <div className="border-t border-border-subtle pt-12">{acts(request, true)}</div>
                  )}
                </CardBody>
              </Card>
            ))}
            {rows.length === 0 && (
              <Card flush>
                <CardBody>{empty}</CardBody>
              </Card>
            )}
          </div>
        </>
      )}
      {canAnswer && (
        <ConfirmDialog
          open={declining !== null}
          onOpenChange={(open) => {
            if (!open && !answer.isPending) setDeclining(null);
          }}
          title={R.declineTitle}
          body={declining ? R.declinePerson(declining.display_name, campaignName) : ''}
          confirmLabel={R.declineConfirm}
          tone="danger"
          testId="requests-person-decline-dialog"
          confirmTestId="requests-person-decline-confirm"
          busy={answer.isPending}
          onConfirm={() => {
            if (declining && !answer.isPending)
              answer.mutate({ request: declining, act: 'decline' });
          }}
        />
      )}
    </section>
  );
}

// ── Campaigns' requests (the Layers tab) ──────────────────────────────────────────────────────────

/**
 * Whether the asking campaign fits this one's charity choice, by the attach's own checks (`fit`). The
 * recipients refusal in the module's own words (FCM-WEB25's 10b pattern, from the parent's side); any
 * other refusal (depth, size, a state) in the server's.
 */
function FitLine({ request }: { request: P2pLayerRequestOut }) {
  const names = useRecipientNames(request.recipients);
  const list = request.parent_recipient_choice === 'supporters';
  if (!request.fit || request.fit.can_add) {
    if (!names.ready || names.items.length === 0) return null;
    const charities = names.items.map((item) => item.name);
    return (
      <p
        className="flex items-start gap-6 text-s13 leading-h15 text-text-secondary"
        data-testid={`requests-campaign-fit-${request.id}`}
      >
        <Icon icon={CircleCheck} size={14} aria-hidden className="mt-2 shrink-0 text-success-700" />
        <span>{list ? R.fitsList(charities) : R.fitsCampaign(charities)}</span>
      </p>
    );
  }
  const words =
    request.fit.refusal_code === 'CAMPAIGN_LAYER_RECIPIENTS'
      ? list
        ? R.noFitList
        : R.noFitCampaign
      : (request.fit.refusal ?? R.noFitCampaign);
  return (
    <StateNotice icon={CircleAlert} testId={`requests-campaign-no-fit-${request.id}`} action={null}>
      {words}
    </StateNotice>
  );
}

function campaignNameOf(request: P2pLayerRequestOut): string {
  return request.campaign_name ?? C.anotherCampaign;
}

function runByOf(request: P2pLayerRequestOut): string {
  return request.organization_name
    ? R.runBy(request.organization_name)
    : C.runBy(request.owner_kind);
}

export function CampaignRequests({
  campaignId,
  campaignName,
  canAnswer,
  enabled,
}: {
  campaignId: string;
  campaignName: string;
  /** The moderation rank on this campaign with the module's write grant. */
  canAnswer: boolean;
  enabled: boolean;
}) {
  const client = useQueryClient();
  const live = useLivePage();
  const read = useQuery({ ...campaignRequestsOptions(campaignId), enabled, retry: false });
  const [dialog, setDialog] = useState<{
    act: 'approve' | 'decline';
    request: P2pLayerRequestOut;
  } | null>(null);
  const answer = useMutation({
    mutationFn: async ({
      request,
      act,
    }: {
      request: P2pLayerRequestOut;
      act: 'approve' | 'decline';
    }) => {
      const path = { fundraiser_id: campaignId, request_id: request.id };
      if (act === 'approve')
        await sdk.campaignsApproveCampaignLayerRequest({ path, throwOnError: true });
      else await sdk.campaignsDeclineCampaignLayerRequest({ path, throwOnError: true });
    },
    onSuccess: async (_data, { act }) => {
      // An approval runs the attach: the tree, both campaigns' counts and the lists all move.
      await Promise.all([
        invalidateRequestReads(client),
        invalidateLayerReads(client),
        client.invalidateQueries({ queryKey: campaignOptions(campaignId).queryKey }),
      ]);
      if (live()) {
        setDialog(null);
        toast.success(act === 'approve' ? R.approvedToast : R.declinedToast);
      }
    },
    // A refused approval (it no longer fits, the structure moved) leaves the request waiting: the
    // server's words, and the card and the tree re-read (a 409 means the structure moved, the module's rule).
    onError: (error) => {
      if (!live()) return;
      setDialog(null);
      toast.error(answerRefusal(error));
      void Promise.all([invalidateRequestReads(client), invalidateLayerReads(client)]);
    },
  });

  if (!enabled || read.isPending || (read.isError && notServed(read.error))) return null;
  const rows = read.data ?? [];
  const waiting = rows.filter((request) => request.status === 'pending');
  const earlier = rows.filter((request) => request.status !== 'pending');
  return (
    <Card flush testId="requests-campaigns">
      <div className="border-b border-border-subtle px-20 py-16">
        <h3 className="font-sans text-s16 font-w600 tracking-givmo text-text-strong">
          {R.requestsTitle}
        </h3>
        <p className="mt-2 font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary">
          {R.campaignsSubtitle}
        </p>
      </div>
      {read.isError ? (
        <CardBody>
          <RequestsUnavailable
            testId="requests-campaigns-error"
            onRetry={() => void read.refetch()}
          />
        </CardBody>
      ) : waiting.length === 0 ? (
        <CardBody>
          <EmptyState
            testId="requests-campaigns-empty"
            icon={<Icon icon={Inbox} size={20} aria-hidden />}
            title={R.emptyTitle}
            description={R.emptyCampaigns}
          />
        </CardBody>
      ) : (
        <ul className="divide-y divide-border-subtle" aria-label={R.requestsTitle}>
          {waiting.map((request) => (
            <li
              key={request.id}
              className="flex flex-col gap-12 px-20 py-16 lg:flex-row lg:items-start lg:gap-20"
              data-testid={`requests-campaign-${request.id}`}
            >
              <div className="flex min-w-0 flex-1 flex-col gap-8">
                <div>
                  <p className="break-words text-s14 font-w600 text-text-strong">
                    {campaignNameOf(request)}
                  </p>
                  <p className="mt-2 text-s12 leading-h15 text-text-secondary">
                    {runByOf(request)} · {R.asked(displayP2pDate(request.created_at))} ·{' '}
                    {R.expires(displayP2pDate(request.expires_at))}
                  </p>
                </div>
                {request.removed_at && (
                  <StateNotice
                    icon={Info}
                    testId={`requests-campaign-removed-${request.id}`}
                    action={null}
                  >
                    {R.removedBefore(displayP2pDate(request.removed_at))}
                  </StateNotice>
                )}
                <FitLine request={request} />
                <p className="text-s13 leading-h15 text-text-secondary">
                  {R.itsLayers(request.layers.names, request.layers.count)}
                </p>
                <a
                  href={request.public_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="relative inline-flex w-fit items-center gap-4 text-s13 font-w600 text-brand-700 underline-offset-2 after:absolute after:inset-x-0 after:-inset-y-2 hover:underline max-lg:after:-inset-y-12"
                  {...testIdProp(`requests-campaign-page-${request.id}`)}
                >
                  {R.viewPage}
                  <Icon icon={ExternalLink} size={13} aria-hidden />
                </a>
              </div>
              {canAnswer && (
                <div className="flex shrink-0 flex-wrap items-center gap-8 lg:justify-end">
                  <Button
                    appearance="saas"
                    tone="secondary"
                    size="sm"
                    className={PHONE_HIT}
                    disabled={answer.isPending}
                    onClick={() => setDialog({ act: 'decline', request })}
                    testId={`requests-campaign-decline-${request.id}`}
                  >
                    {R.decline}
                  </Button>
                  <Button
                    appearance="saas"
                    size="sm"
                    className={PHONE_HIT}
                    // A request that can't pass says why above, and Approve waits (decision 5).
                    disabled={answer.isPending || request.fit?.can_add === false}
                    onClick={() => setDialog({ act: 'approve', request })}
                    testId={`requests-campaign-approve-${request.id}`}
                  >
                    {R.approve}
                  </Button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {!read.isError && earlier.length > 0 && (
        <div
          className="border-t border-border-subtle px-20 py-16"
          data-testid="requests-campaigns-earlier"
        >
          <p className="mb-8 text-s12 font-w600 tracking-givmo text-text-secondary">{R.earlier}</p>
          <ul className="flex flex-col gap-10">
            {earlier.map((request) => (
              <li
                key={request.id}
                className="flex items-start justify-between gap-12"
                data-testid={`requests-campaign-earlier-${request.id}`}
              >
                <div className="min-w-0">
                  <p className="text-s14 text-text-strong">{campaignNameOf(request)}</p>
                  <p className="text-s12 leading-h15 text-text-secondary">
                    {runByOf(request)} ·{' '}
                    {decidedLine(
                      request,
                      request.status !== 'cancelled'
                        ? null
                        : request.cancel_reason === 'child_ended'
                          ? R.whenItEnded
                          : request.cancel_reason === 'joined_elsewhere'
                            ? R.whenItJoinedElsewhere
                            : request.cancel_reason === 'parent_ended'
                              ? R.whenThisEnded
                              : null,
                    )}
                  </p>
                </div>
                <Badge tone="neutral" className="shrink-0">
                  {R.status[request.status] ?? request.status}
                </Badge>
              </li>
            ))}
          </ul>
        </div>
      )}
      {canAnswer && (
        <>
          <ConfirmDialog
            open={dialog?.act === 'approve'}
            onOpenChange={(open) => {
              if (!open && !answer.isPending) setDialog(null);
            }}
            title={R.approveTitle}
            body={
              dialog?.act === 'approve'
                ? R.approveBody(campaignNameOf(dialog.request), campaignName)
                : ''
            }
            confirmLabel={R.approveConfirm}
            testId="requests-campaign-approve-dialog"
            confirmTestId="requests-campaign-approve-confirm"
            busy={answer.isPending}
            onConfirm={() => {
              if (dialog && !answer.isPending)
                answer.mutate({ request: dialog.request, act: 'approve' });
            }}
          />
          <ConfirmDialog
            open={dialog?.act === 'decline'}
            onOpenChange={(open) => {
              if (!open && !answer.isPending) setDialog(null);
            }}
            title={R.declineTitle}
            body={
              dialog?.act === 'decline'
                ? R.declineCampaign(campaignNameOf(dialog.request), campaignName)
                : ''
            }
            confirmLabel={R.declineConfirm}
            tone="danger"
            testId="requests-campaign-decline-dialog"
            confirmTestId="requests-campaign-decline-confirm"
            busy={answer.isPending}
            onConfirm={() => {
              if (dialog && !answer.isPending)
                answer.mutate({ request: dialog.request, act: 'decline' });
            }}
          />
        </>
      )}
    </Card>
  );
}
